import React, { useState, useEffect } from 'react';
import PreLoginComponent from './components/auth/PreLoginComponent.jsx';
import MainLoginComponent from './components/auth/MainLoginComponent.jsx';
import Dashboard from './components/dashboard/Dashboard.jsx';
import { ThemeProvider } from './contexts/ThemeContext.jsx';
import { authService } from './services/authService.js';
import { activityTracker } from './services/activityTracker.js';
import { crmService } from './services/crmService.js';

// Toast Notification Component
function ToastNotification({ message, type = 'info', onClose }) {
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
        }, 5000);
        return () => clearTimeout(timer);
    }, []);

    const bgColors = {
        info: 'bg-blue-500',
        success: 'bg-green-500',
        warning: 'bg-yellow-500',
        error: 'bg-red-500'
    };

    return (
        <div className={`fixed top-4 right-4 ${bgColors[type]} text-white px-6 py-4 rounded-lg shadow-lg z-50 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'} max-w-md`}>
            <div className="flex items-center justify-between gap-4">
                <span className="font-medium">{message}</span>
                <button
                    onClick={() => { setIsVisible(false); setTimeout(onClose, 300); }}
                    className="text-white hover:text-gray-200 font-bold text-xl leading-none"
                >
                    ✕
                </button>
            </div>
        </div>
    );
}

// Main App Component
function App() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('crm_theme') === 'dark');

    // Apply theme attribute to <html> whenever darkMode changes
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
        localStorage.setItem('crm_theme', darkMode ? 'dark' : 'light');
    }, [darkMode]);

    useEffect(() => {
        const restoreSession = async () => {
            const localSession = localStorage.getItem('crm_session');
            if (localSession) {
                try {
                    // Cached UI state alone isn't enough to trust anymore - confirm
                    // there's still a real, signed Supabase Auth session behind it,
                    // and re-fetch the profile fresh (role/team_id may have changed
                    // since login - an admin editing this user shouldn't require
                    // them to log out and back in before it takes effect).
                    const freshProfile = await authService.refreshProfile();
                    if (freshProfile) {
                        setUser(freshProfile);
                        localStorage.setItem('crm_session', JSON.stringify(freshProfile));
                        document.body.className = 'bg-gray-100';
                        if (freshProfile.username) {
                            activityTracker.startTracking(freshProfile.username);
                        }
                    } else {
                        localStorage.removeItem('crm_session');
                    }
                } catch {
                    localStorage.removeItem('crm_session');
                }
            }
            setLoading(false);
        };
        restoreSession();
    }, []);

    const handleLogin = async (userData) => {
        setUser(userData);
        document.body.className = 'bg-gray-100';
        localStorage.setItem('crm_session', JSON.stringify(userData));

        try {
            if (userData.username) {
                await crmService.updateUser(userData.username, { last_login: new Date().toISOString() });
            }
        } catch { /* non-critical */ }

        if (userData.username) {
            activityTracker.startTracking(userData.username);
        }
    };

    const handleLogout = async () => {
        if (user?.username) {
            activityTracker.stopTracking(user.username);
        }
        await authService.logout();
        localStorage.removeItem('crm_session');
        localStorage.removeItem('crm_prelogin');
        setUser(null);
        document.body.className = 'white-bg';
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 mb-2">Loading...</div>
                </div>
            </div>
        );
    }

    const isPreLoginComplete = localStorage.getItem('crm_prelogin') === 'true';

    if (!isPreLoginComplete) {
        return <PreLoginComponent onPreLoginSuccess={() => window.location.reload()} />;
    }

    if (!user) {
        return <MainLoginComponent onLoginSuccess={handleLogin} />;
    }

    return (
        <ThemeProvider user={user}>
            <Dashboard user={user} onLogout={handleLogout} />
        </ThemeProvider>
    );
}

export default App;
